import React, { useState, useEffect } from "react";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Checkbox from "@material-ui/core/Checkbox";
import axios from "axios";

// material-ui icons
import Person from "@material-ui/icons/Person";
import Edit from "@material-ui/icons/Edit";
import Close from "@material-ui/icons/Close";
import Check from "@material-ui/icons/Check";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Table from "components/Table/Table.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import AssessmentCard from "./AssessmentCard.js";

import styles from "assets/jss/elite-feats-cms/views/extendedTablesStyle.js";

import AppContext from "../../AppContext";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(styles);

export default function ExtendedTables(props) {
  const { t } = useTranslation();
  let assignment = JSON.parse(localStorage.getItem("assignment"));
  let order = JSON.parse(localStorage.getItem("order"));
  const {
    totalPurchased,
    totalUsed,
    pending,
    completed,
    subscriptionData,
    isMobile,
  } = props;
  const cartContext = React.useContext(AppContext);
  let initialChecked = [];
  for (let number in assignment) {
    if (assignment[number] === true) {
      initialChecked.push(number);
    }
  }

  const [checked, setChecked] = useState(initialChecked);
  const [subscriptionAvailable, setSubscriptionAvailable] = useState(0);
  const [loading, setLoading] = useState(true);
  const [effectiveAvailableUnits, setEffectiveAvailableUnits] = useState({
    tool4_1: totalPurchased.tool4_1 - totalUsed.tool4_1,
    tool4_2: totalPurchased.tool4_2 - totalUsed.tool4_2,
    tool4_3: totalPurchased.tool4_3 - totalUsed.tool4_3,
    tool4_4: totalPurchased.tool4_4 - totalUsed.tool4_4,
    tool4_5: totalPurchased.tool4_5 - totalUsed.tool4_5,
  });

  // Fetch subscription data
  useEffect(() => {
    try {
      setLoading(true);
      const availableUnits =
        subscriptionData && subscriptionData.active
          ? subscriptionData.totalAvailable
          : 0;

      setSubscriptionAvailable(availableUnits);

      // Update the effective available units
      setEffectiveAvailableUnits({
        tool4_1:
          totalPurchased.tool4_1 -
          totalUsed.tool4_1 +
          (availableUnits > 0 ? 1 : 0),
        tool4_2:
          totalPurchased.tool4_2 -
          totalUsed.tool4_2 +
          (availableUnits > 0 ? 1 : 0),
        tool4_3:
          totalPurchased.tool4_3 -
          totalUsed.tool4_3 +
          (availableUnits > 0 ? 1 : 0),
        tool4_4:
          totalPurchased.tool4_4 -
          totalUsed.tool4_4 +
          (availableUnits > 0 ? 1 : 0),
        tool4_5:
          totalPurchased.tool4_5 -
          totalUsed.tool4_5 +
          (availableUnits > 0 ? 1 : 0),
      });
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      // Fallback to just purchased - used
      setEffectiveAvailableUnits({
        tool4_1: totalPurchased.tool4_1 - totalUsed.tool4_1,
        tool4_2: totalPurchased.tool4_2 - totalUsed.tool4_2,
        tool4_3: totalPurchased.tool4_3 - totalUsed.tool4_3,
        tool4_4: totalPurchased.tool4_4 - totalUsed.tool4_4,
        tool4_5: totalPurchased.tool4_5 - totalUsed.tool4_5,
      });
    } finally {
      setLoading(false);
    }
  }, [totalPurchased, totalUsed]);

  useEffect(() => {
    if (!order.length && checked.length) {
      setChecked([]);
    }
  });

  const handleToggle = (value) => {
    const currentIndex = checked.indexOf(value.toString());
    const orderIndex = order.indexOf(value.toString());
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value.toString());
      order.push(value.toString());
      assignment[`${value}`] = true;
    } else {
      newChecked.splice(currentIndex, 1);
      order.splice(orderIndex, 1);
      assignment[`${value}`] = false;
    }
    setChecked(newChecked);
    localStorage.setItem("assignment", JSON.stringify(assignment));
    localStorage.setItem("order", JSON.stringify(order));
    props.triggerReload();
  };

  const classes = useStyles();
  const fillButtons = [
    { color: "info", icon: Person },
    { color: "success", icon: Edit },
    { color: "danger", icon: Close },
  ].map((prop, key) => {
    return (
      <Button color={prop.color} className={classes.actionButton} key={key}>
        <prop.icon className={classes.icon} />
      </Button>
    );
  });

  const simpleButtons = [
    { color: "info", icon: Person },
    { color: "success", icon: Edit },
    { color: "danger", icon: Close },
  ].map((prop, key) => {
    return (
      <Button
        color={prop.color}
        simple
        className={classes.actionButton}
        key={key}
      >
        <prop.icon className={classes.icon} />
      </Button>
    );
  });

  const roundButtons = [
    { color: "info", icon: Person },
    { color: "success", icon: Edit },
    { color: "danger", icon: Close },
  ].map((prop, key) => {
    return (
      <Button
        round
        color={prop.color}
        className={classes.actionButton + " " + classes.actionButtonRound}
        key={key}
      >
        <prop.icon className={classes.icon} />
      </Button>
    );
  });

  // Tools data for both card and table views
  const tools = [
    {
      number: "4.1",
      name: t("assignments:assessmentNames.tool4_1"),
      purchased: totalPurchased.tool4_1,
      used: totalUsed.tool4_1,
      available: totalPurchased.tool4_1 - totalUsed.tool4_1,
      pending: pending.tool4_1,
      completed: completed.tool4_1,
      effectiveAvailable: effectiveAvailableUnits.tool4_1,
      key: "tool4_1",
    },
    {
      number: "4.2",
      name: t("assignments:assessmentNames.tool4_2"),
      purchased: totalPurchased.tool4_2,
      used: totalUsed.tool4_2,
      available: totalPurchased.tool4_2 - totalUsed.tool4_2,
      pending: pending.tool4_2,
      completed: completed.tool4_2,
      effectiveAvailable: effectiveAvailableUnits.tool4_2,
      key: "tool4_2",
    },
    {
      number: "4.3",
      name: t("assignments:assessmentNames.tool4_3"),
      purchased: totalPurchased.tool4_3,
      used: totalUsed.tool4_3,
      available: totalPurchased.tool4_3 - totalUsed.tool4_3,
      pending: pending.tool4_3,
      completed: completed.tool4_3,
      effectiveAvailable: effectiveAvailableUnits.tool4_3,
      key: "tool4_3",
    },
    {
      number: "4.4",
      name: t("assignments:assessmentNames.tool4_4"),
      purchased: totalPurchased.tool4_4,
      used: totalUsed.tool4_4,
      available: totalPurchased.tool4_4 - totalUsed.tool4_4,
      pending: pending.tool4_4,
      completed: completed.tool4_4,
      effectiveAvailable: effectiveAvailableUnits.tool4_4,
      key: "tool4_4",
    },
    {
      number: "4.5",
      name: t("assignments:assessmentNames.tool4_5"),
      purchased: totalPurchased.tool4_5,
      used: totalUsed.tool4_5,
      available: totalPurchased.tool4_5 - totalUsed.tool4_5,
      pending: pending.tool4_5,
      completed: completed.tool4_5,
      effectiveAvailable: effectiveAvailableUnits.tool4_5,
      key: "tool4_5",
    },
  ];

  // If mobile, render card view
  if (isMobile) {
    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card plain style={{ marginTop: -10, marginBottom: 0 }}>
            <CardBody>
              {tools.map((tool) => (
                <AssessmentCard
                  key={tool.number}
                  name={tool.name}
                  purchased={tool.purchased}
                  used={tool.used}
                  available={`${tool.available}${
                    subscriptionAvailable > 0
                      ? " + " + subscriptionAvailable
                      : ""
                  }`}
                  pending={tool.pending}
                  completed={tool.completed}
                  action={
                    tool.effectiveAvailable > 0 ? (
                      <Checkbox
                        key="key"
                        tabIndex={-1}
                        style={{ padding: 7 }}
                        onClick={() => handleToggle(tool.number)}
                        checked={assignment[tool.number]}
                        checkedIcon={<Check className={classes.checkedIcon} />}
                        icon={<Check className={classes.uncheckedIcon} />}
                        classes={{
                          checked: classes.checkedDarkBlue,
                          root: classes.checkRoot,
                        }}
                      />
                    ) : (
                      <Button
                        color="tool4"
                        onClick={() => {
                          let currentCart = JSON.parse(
                            localStorage.getItem("cart")
                          );
                          currentCart[tool.key] += 1;
                          localStorage.setItem(
                            "cart",
                            JSON.stringify(currentCart)
                          );
                          cartContext.setCart(currentCart);
                        }}
                        round
                        size="sm"
                        style={{ padding: 4, paddingLeft: 15, paddingRight: 15 }}
                      >
                        {t("assignments:addToCart")}
                      </Button>
                    )
                  }
                />
              ))}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }

  // Desktop: render table view
  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card plain style={{ marginTop: -10, marginBottom: 0 }}>
          <CardBody>
            <Table
              tableHead={[
                t("assignments:tableHeaders.assessment"),
                t("assignments:tableHeaders.purchased"),
                t("assignments:tableHeaders.used"),
                t("assignments:tableHeaders.available"),
                t("assignments:tableHeaders.pending"),
                t("assignments:tableHeaders.completed"),
                t("assignments:tableHeaders.assign"),
              ]}
              tableData={[
                [
                  t("assignments:assessmentNames.tool4_1"),
                  `${totalPurchased.tool4_1}`,
                  `${totalUsed.tool4_1}`,
                  `${totalPurchased.tool4_1 - totalUsed.tool4_1}${
                    subscriptionAvailable > 0
                      ? " + " + subscriptionAvailable
                      : ""
                  }`,
                  `${pending.tool4_1}`,
                  `${completed.tool4_1}`,
                  effectiveAvailableUnits.tool4_1 > 0 ? (
                    <Checkbox
                      key="key"
                      tabIndex={-1}
                      style={{ padding: 7 }}
                      onClick={() => handleToggle(4.1)}
                      checked={assignment["4.1"]}
                      checkedIcon={<Check className={classes.checkedIcon} />}
                      icon={<Check className={classes.uncheckedIcon} />}
                      classes={{
                        checked: classes.checkedGreen,
                        root: classes.checkRoot,
                      }}
                    />
                  ) : (
                    <Button
                      color="toolsFour"
                      round
                      size="sm"
                      style={{ padding: 4, paddingLeft: 15, paddingRight: 15 }}
                      onClick={() => {
                        let currentCart = JSON.parse(
                          localStorage.getItem("cart")
                        );
                        currentCart["tool4_1"] += 1;
                        localStorage.setItem(
                          "cart",
                          JSON.stringify(currentCart)
                        );
                        cartContext.setCart(currentCart);
                      }}
                    >
                      {t("assignments:addToCart")}
                    </Button>
                  ),
                ],
                [
                  t("assignments:assessmentNames.tool4_2"),
                  `${totalPurchased.tool4_2}`,
                  `${totalUsed.tool4_2}`,
                  `${totalPurchased.tool4_2 - totalUsed.tool4_2}${
                    subscriptionAvailable > 0
                      ? " + " + subscriptionAvailable
                      : ""
                  }`,
                  `${pending.tool4_2}`,
                  `${completed.tool4_2}`,
                  effectiveAvailableUnits.tool4_2 > 0 ? (
                    <Checkbox
                      key="key"
                      tabIndex={-1}
                      style={{ padding: 7 }}
                      onClick={() => handleToggle(4.2)}
                      checked={assignment["4.2"]}
                      checkedIcon={<Check className={classes.checkedIcon} />}
                      icon={<Check className={classes.uncheckedIcon} />}
                      classes={{
                        checked: classes.checkedGreen,
                        root: classes.checkRoot,
                      }}
                    />
                  ) : (
                    <Button
                      color="toolsFour"
                      round
                      size="sm"
                      style={{ padding: 4, paddingLeft: 15, paddingRight: 15 }}
                      onClick={() => {
                        let currentCart = JSON.parse(
                          localStorage.getItem("cart")
                        );
                        currentCart["tool4_2"] += 1;
                        localStorage.setItem(
                          "cart",
                          JSON.stringify(currentCart)
                        );
                        cartContext.setCart(currentCart);
                      }}
                    >
                      {t("assignments:addToCart")}
                    </Button>
                  ),
                ],
                [
                  t("assignments:assessmentNames.tool4_3"),
                  `${totalPurchased.tool4_3}`,
                  `${totalUsed.tool4_3}`,
                  `${totalPurchased.tool4_3 - totalUsed.tool4_3}${
                    subscriptionAvailable > 0
                      ? " + " + subscriptionAvailable
                      : ""
                  }`,
                  `${pending.tool4_3}`,
                  `${completed.tool4_3}`,
                  effectiveAvailableUnits.tool4_3 > 0 ? (
                    <Checkbox
                      key="key"
                      tabIndex={-1}
                      style={{ padding: 7 }}
                      onClick={() => handleToggle(4.3)}
                      checked={assignment["4.3"]}
                      checkedIcon={<Check className={classes.checkedIcon} />}
                      icon={<Check className={classes.uncheckedIcon} />}
                      classes={{
                        checked: classes.checkedGreen,
                        root: classes.checkRoot,
                      }}
                    />
                  ) : (
                    <Button
                      color="toolsFour"
                      round
                      size="sm"
                      style={{ padding: 4, paddingLeft: 15, paddingRight: 15 }}
                      onClick={() => {
                        let currentCart = JSON.parse(
                          localStorage.getItem("cart")
                        );
                        currentCart["tool4_3"] += 1;
                        localStorage.setItem(
                          "cart",
                          JSON.stringify(currentCart)
                        );
                        cartContext.setCart(currentCart);
                      }}
                    >
                      {t("assignments:addToCart")}
                    </Button>
                  ),
                ],
                [
                  t("assignments:assessmentNames.tool4_4"),
                  `${totalPurchased.tool4_4}`,
                  `${totalUsed.tool4_4}`,
                  `${totalPurchased.tool4_4 - totalUsed.tool4_4}${
                    subscriptionAvailable > 0
                      ? " + " + subscriptionAvailable
                      : ""
                  }`,
                  `${pending.tool4_4}`,
                  `${completed.tool4_4}`,
                  effectiveAvailableUnits.tool4_4 > 0 ? (
                    <Checkbox
                      key="key"
                      tabIndex={-1}
                      style={{ padding: 7 }}
                      onClick={() => handleToggle(4.4)}
                      checked={assignment["4.4"]}
                      checkedIcon={<Check className={classes.checkedIcon} />}
                      icon={<Check className={classes.uncheckedIcon} />}
                      classes={{
                        checked: classes.checkedGreen,
                        root: classes.checkRoot,
                      }}
                    />
                  ) : (
                    <Button
                      color="toolsFour"
                      round
                      size="sm"
                      style={{ padding: 4, paddingLeft: 15, paddingRight: 15 }}
                      onClick={() => {
                        let currentCart = JSON.parse(
                          localStorage.getItem("cart")
                        );
                        currentCart["tool4_4"] += 1;
                        localStorage.setItem(
                          "cart",
                          JSON.stringify(currentCart)
                        );
                        cartContext.setCart(currentCart);
                      }}
                    >
                      {t("assignments:addToCart")}
                    </Button>
                  ),
                ],
                [
                  t("assignments:assessmentNames.tool4_5"),
                  `${totalPurchased.tool4_5}`,
                  `${totalUsed.tool4_5}`,
                  `${totalPurchased.tool4_5 - totalUsed.tool4_5}${
                    subscriptionAvailable > 0
                      ? " + " + subscriptionAvailable
                      : ""
                  }`,
                  `${pending.tool4_5}`,
                  `${completed.tool4_5}`,
                  effectiveAvailableUnits.tool4_5 > 0 ? (
                    <Checkbox
                      key="key"
                      tabIndex={-1}
                      style={{ padding: 7 }}
                      onClick={() => handleToggle(4.5)}
                      checked={assignment["4.5"]}
                      checkedIcon={<Check className={classes.checkedIcon} />}
                      icon={<Check className={classes.uncheckedIcon} />}
                      classes={{
                        checked: classes.checkedGreen,
                        root: classes.checkRoot,
                      }}
                    />
                  ) : (
                    <Button
                      color="toolsFour"
                      round
                      size="sm"
                      style={{ padding: 4, paddingLeft: 15, paddingRight: 15 }}
                      onClick={() => {
                        let currentCart = JSON.parse(
                          localStorage.getItem("cart")
                        );
                        currentCart["tool4_5"] += 1;
                        localStorage.setItem(
                          "cart",
                          JSON.stringify(currentCart)
                        );
                        cartContext.setCart(currentCart);
                      }}
                    >
                      {t("assignments:addToCart")}
                    </Button>
                  ),
                ],
              ]}
              customCellClasses={[
                classes.left,
                classes.center,
                classes.center,
                classes.center,
                classes.center,
                classes.center,
                classes.center,
              ]}
              customClassesForCells={[0, 1, 2, 3, 4, 5, 6]}
              customHeadCellClasses={[
                classes.left,
                classes.center,
                classes.center,
                classes.center,
                classes.center,
                classes.center,
                classes.center,
              ]}
              customHeadClassesForCells={[0, 1, 2, 3, 4, 5, 6]}
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
